import { fetchWithAuth } from '@/lib/utils/api-auth';

export interface OrgInviteNotification {
  id: string;
  orgId: string;
  orgName: string;
  inviterName: string;
  role: string;
  token: string;
  createdAt: string;
  expiresAt: string;
}

export interface ProjectInviteNotification {
  id: string;
  projectId: string;
  projectName: string;
  inviterName: string;
  role: string;
  token: string;
  createdAt: string;
  expiresAt: string;
}

export interface CommentNotification {
  id: string;
  userId: string;
  projectId: string | null;
  kind: string; // 'comment_pin' | 'comment_reply'
  title: string;
  body: string;
  link: string;
  read: boolean;
  createdAt: string;
}

/**
 * AdminNotification — admin-only ops items added 2026-05-13 by Phase 7D.
 * The Go API only includes these in the response when the calling
 * principal has Role=ADMIN; for other users the field is an empty array.
 *
 * `kind` mirrors severity:
 *   - "info"    → neutral system notice
 *   - "warning" → degraded state, attention needed
 *   - "error"   → ops critical, operator action required
 *
 * Surface: Posteingang (Inbox) tab in NotificationPanel, alongside
 * org/project invites + comment notifications.
 */
export interface AdminNotification {
  id: string;
  kind: 'info' | 'warning' | 'error' | string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
}

export interface MyNotifications {
  orgInvites: OrgInviteNotification[];
  projectInvites: ProjectInviteNotification[];
  commentNotifications: CommentNotification[];
  unreadCommentCount: number;
  // Phase 7D 2026-05-13 — admin-only ops alerts in Posteingang.
  // Empty array for non-admin users (Go-side audience filter).
  adminNotifications: AdminNotification[];
  unreadAdminCount: number;
}

export async function fetchMyNotifications(opts?: { silent?: boolean }): Promise<MyNotifications> {
  // `silent: true` is passed by the periodic poll in NotificationContext so a
  // server-side idle-close timing race surfaces as an AbortError-shaped throw
  // that the caller silently ignores instead of bubbling a console error.
  const res = await fetchWithAuth('/api/me/notifications', { silent: opts?.silent });
  if (!res.ok) {
    throw new Error('Failed to load notifications');
  }
  const json = (await res.json()) as { success: boolean; data: MyNotifications };
  return json.data;
}

export async function markNotificationRead(id: string): Promise<void> {
  const res = await fetchWithAuth(`/api/me/notifications/${id}/read`, {
    method: 'PUT',
  });
  if (!res.ok) {
    throw new Error('Failed to mark notification as read');
  }
}

export async function markAllNotificationsRead(): Promise<void> {
  const res = await fetchWithAuth('/api/me/notifications/read-all', {
    method: 'PUT',
  });
  if (!res.ok) {
    throw new Error('Failed to mark all notifications as read');
  }
}

/**
 * Phase 7D 2026-05-13 — admin-only AdminNotification mark-read helpers.
 *
 * The endpoints return 404 (not 403) for non-admin callers — the
 * existence of the admin-notifications surface is itself an admin-tier
 * signal we keep quiet. Both helpers are safe to call from any user;
 * for non-admins they simply throw which the caller can swallow.
 */
export async function markAdminNotificationRead(id: string): Promise<void> {
  const res = await fetchWithAuth(`/api/me/admin-notifications/${id}/read`, {
    method: 'PUT',
  });
  if (!res.ok) {
    throw new Error('Failed to mark admin notification as read');
  }
}

export async function markAllAdminNotificationsRead(): Promise<void> {
  const res = await fetchWithAuth('/api/me/admin-notifications/read-all', {
    method: 'PUT',
  });
  if (!res.ok) {
    throw new Error('Failed to mark all admin notifications as read');
  }
}
